import { Flex, Text } from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import { Sample } from '@texas/api/endpoints/samplesApi';
import { EntityType } from '@texas/api/endpoints/trackerApi';
import { AngularNavLink } from '@texas/components/shared/AngularNavLink';
import { evaluateSampleNextAction } from '@texas/components/shared/samples/evaluateSampleNextAction';
import { SampleNextAction } from '@texas/components/shared/samples/SampleNextAction';
import { FilteredTagsCell } from '@texas/components/shared/tags/FilteredTagsCell';
import { TagColumnHeader } from '@texas/components/shared/tags/TagColumnHeader';
import { TrackerChanges } from '@texas/components/shared/tracker/TrackerChanges';
import { UserBadge } from '@texas/components/shared/users/UserBadge';
import { ensureEnumKey } from '@texas/utils/helpers/enumHelpers';
import { TFunction } from 'i18next';

export interface SamplesColumnsBaseProps {
  t: TFunction;
  hideStopFollowing?: boolean;
}
interface Props extends SamplesColumnsBaseProps {
  columns: {
    afterIndex: number;
    columns: any[];
  };
}

export const sampleColumnsCreator = ({
  t,
  columns,
  hideStopFollowing,
}: Props) => {
  const columnHelper = createColumnHelper<Sample>();
  const cols = [
    columnHelper.accessor('sampleId', {
      cell: (info) => {
        const sample = info.row.original;
        const state = sample.lifecycle;

        const nextAction = evaluateSampleNextAction({
          state,
          sampleId: info.getValue(),
          versionArchived: sample.compositionGroupArchived,
        });

        return (
          <TrackerChanges
            hasChanged={info.row.original.isChangedSinceLastTrack}
            changes={info.row.original.changes}
          >
            <SampleNextAction
              action={nextAction}
              lifecycle={state}
              transportRequestId={info.row.original.transport?.id}
              pa={{
                id: info.row.original.productApproval.productApprovalId,
                state: info.row.original.productApproval.state,
              }}
              sampleId={info.row.original.sampleId}
              inPossessionOfUserId={info.row.original.inPossessionOfUser.id}
              groupId={info.row.original.compositionGroupId}
              variantId={info.row.original.variantId}
              supplierId={info.row.original.supplierId}
              articleFolderId={info.row.original.articleFolderId}
              shipmentId={info.row.original.transport?.shipment?.id}
              branchId={info.row.original.branchId}
              hideStopFollowing={hideStopFollowing}
            />
          </TrackerChanges>
        );
      },
      maxSize: 60,
      header: '',
      enableSorting: false,
    }),
    columnHelper.accessor('articleName', {
      cell: (info) => (
        <AngularNavLink
          to="variant.versions"
          navobj={{
            articleId: info.row.original.articleId,
            variantId: info.row.original.variantId,
          }}
        >
          <Flex flexDir="column">
            <Text fontWeight="bold">{info.row.original.articleItemCode}</Text>
            <Text>{info.row.original.articleName}</Text>
          </Flex>
        </AngularNavLink>
      ),
      header: 'Article',
      meta: { toggleable: true, defaultVisible: true },
    }),
    columnHelper.accessor('matrixNodeXValue', {
      cell: (info) => (
        <>
          {info.row.original.matrixNodeXValue && (
            <>
              <Text>{info.row.original.matrixNodeXValue}</Text>
              <Text>{info.row.original.matrixNodeYValue}</Text>
            </>
          )}
        </>
      ),
      header: '',
    }),
    columnHelper.accessor('customerName', {
      cell: (info) => (
        <AngularNavLink
          to="customer"
          navobj={{
            customerId: info.row.original.customerId,
          }}
        >
          {info.getValue()}
        </AngularNavLink>
      ),
      header: t('general.brand'),
      enableSorting: false,
      meta: { toggleable: true, defaultVisible: true },
    }),
    columnHelper.accessor('supplierName', {
      cell: (info) => info.getValue(),
      header: 'Supplier',
      meta: { toggleable: true, defaultVisible: true },
    }),
    columnHelper.accessor('branchName', {
      cell: (info) => info.getValue(),
      header: 'Branch',
      meta: { toggleable: true, defaultVisible: true },
    }),
    columnHelper.accessor('productApproval.formatNumber', {
      cell: (info) => (
        <AngularNavLink
          to="article.productApproval"
          navobj={{
            articleId: info.row.original.articleId,
            searchTerm: info.row.original.productApproval.number,
            state: info.row.original.productApproval.state,
          }}
        >
          {info.getValue()}
        </AngularNavLink>
      ),
      header: 'Number',
      meta: { toggleable: true, defaultVisible: true },
    }),
    columnHelper.accessor('inPossessionOfUser.id', {
      cell: (info) => (
        <UserBadge
          name={info.row.original.inPossessionOfUser.name}
          userId={info.row.original.inPossessionOfUser.id}
          branch={info.row.original.inPossessionOfUser.branchIdentifier}
        />
      ),
      header: 'Owner',
      meta: { toggleable: true, defaultVisible: true },
    }),
    columnHelper.accessor('tags', {
      cell: (info) => {
        const tags = info.getValue();
        const articleTags =
          tags[
            ensureEnumKey(
              EntityType,
              EntityType.Article,
            ) as unknown as EntityType
          ];
        if (!articleTags || articleTags.length === 0) return null;

        return <FilteredTagsCell tags={articleTags} />;
      },
      header: () => <TagColumnHeader />,
      enableSorting: false,
      meta: { toggleable: true, defaultVisible: true },
    }),
  ];

  cols.splice(columns.afterIndex, 0, ...columns.columns);

  return cols;
};
